import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

export function useRealtimeContracts() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('contract-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contracts',
        },
        (payload) => {
          const oldData = payload.old as { status?: string; viewed_at?: string; signed_at?: string };
          const newData = payload.new as { 
            id: string; 
            title: string; 
            status?: string; 
            viewed_at?: string; 
            signed_at?: string;
            signer_name?: string;
          };

          // Check if contract was viewed
          if (!oldData.viewed_at && newData.viewed_at) {
            toast.info(`Contract "${newData.title}" was viewed`, {
              description: newData.signer_name ? `by ${newData.signer_name}` : undefined,
            });
          }

          // Check if contract was signed
          if (!oldData.signed_at && newData.signed_at) {
            toast.success(`Contract "${newData.title}" was signed!`, {
              description: newData.signer_name ? `by ${newData.signer_name}` : undefined,
            });
          }

          // Check for status changes
          if (oldData.status !== newData.status) {
            const statusMessages: Record<string, string> = {
              sent: 'Contract sent for signature',
              viewed: 'Contract viewed by recipient',
              signed: 'Contract signed successfully',
              expired: 'Contract has expired',
            };
            
            if (statusMessages[newData.status || '']) {
              const isSuccess = newData.status === 'signed';
              if (isSuccess) {
                toast.success(statusMessages[newData.status!], {
                  description: newData.title,
                });
              } else {
                toast.info(statusMessages[newData.status!], {
                  description: newData.title,
                });
              }
            }
          }

          // Invalidate queries to refresh data
          queryClient.invalidateQueries({ queryKey: ['contracts'] });
          queryClient.invalidateQueries({ queryKey: ['contract-stats'] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'contracts',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['contracts'] });
          queryClient.invalidateQueries({ queryKey: ['contract-stats'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);
}
